import type {
  CanonicalTreeGeometryBranchRoot,
} from './CanonicalTreeGeometryBranchRootAuthority';

export type CanonicalTreeGeometryAncestryRoot =
  | 'maternal'
  | 'paternal';

export interface CanonicalTreeGeometryEnvelopeUnit {
  readonly unitId:
    string;

  readonly root:
    CanonicalTreeGeometryBranchRoot;

  readonly x:
    number;

  readonly width:
    number;
}

export interface CanonicalTreeGeometryRootEnvelopeInput {
  readonly units:
    readonly CanonicalTreeGeometryEnvelopeUnit[];

  readonly viewerCenterX:
    number;

  readonly viewerEnvelopeWidth:
    number;

  readonly rootGap:
    number;
}

export interface CanonicalTreeGeometryEnvelopeBounds {
  readonly minX:
    number;

  readonly maxX:
    number;

  readonly width:
    number;
}

export interface CanonicalTreeGeometryRootEnvelopeResult {
  readonly plannedXByUnitId:
    ReadonlyMap<string, number>;

  readonly viewerEnvelope:
    CanonicalTreeGeometryEnvelopeBounds;

  readonly maternalEnvelope:
    CanonicalTreeGeometryEnvelopeBounds | null;

  readonly paternalEnvelope:
    CanonicalTreeGeometryEnvelopeBounds | null;

  readonly maternalShiftX:
    number;

  readonly paternalShiftX:
    number;

  readonly unaffectedUnitIds:
    readonly string[];

  readonly status:
    | 'PASS'
    | 'NO-ANCESTRY-ROOT-UNITS';
}

const finite = (
  value:
    number,
  name:
    string,
): number => {
  if (!Number.isFinite(value)) {
    throw new Error(
      `Invalid finite geometry value for ${name}.`,
    );
  }

  return value;
};

const nonNegative = (
  value:
    number,
  name:
    string,
): number => {
  const checked =
    finite(value, name);

  if (checked < 0) {
    throw new Error(
      `Geometry value ${name} must be non-negative.`,
    );
  }

  return checked;
};

const boundsFor = (
  units:
    readonly CanonicalTreeGeometryEnvelopeUnit[],

  xByUnitId:
    ReadonlyMap<string, number>,
): CanonicalTreeGeometryEnvelopeBounds | null => {
  if (units.length === 0) {
    return null;
  }

  let minX =
    Number.POSITIVE_INFINITY;

  let maxX =
    Number.NEGATIVE_INFINITY;

  for (const unit of units) {
    const x =
      xByUnitId.get(unit.unitId);

    if (x === undefined) {
      throw new Error(
        `Missing planned x-coordinate for unit ${unit.unitId}.`,
      );
    }

    const halfWidth =
      unit.width / 2;

    minX =
      Math.min(
        minX,
        x - halfWidth,
      );

    maxX =
      Math.max(
        maxX,
        x + halfWidth,
      );
  }

  return Object.freeze({
    minX,
    maxX,
    width:
      maxX - minX,
  });
};

const viewerBounds = (
  centerX:
    number,

  width:
    number,
): CanonicalTreeGeometryEnvelopeBounds => {
  const halfWidth =
    width / 2;

  return Object.freeze({
    minX:
      centerX - halfWidth,

    maxX:
      centerX + halfWidth,

    width,
  });
};

const isAncestryRoot = (
  root:
    CanonicalTreeGeometryBranchRoot,
): root is CanonicalTreeGeometryAncestryRoot =>
  root === 'maternal' ||
  root === 'paternal';

export const planCanonicalTreeGeometryRootEnvelopes = (
  input:
    CanonicalTreeGeometryRootEnvelopeInput,
): CanonicalTreeGeometryRootEnvelopeResult => {
  const viewerCenterX =
    finite(
      input.viewerCenterX,
      'viewerCenterX',
    );

  const viewerEnvelopeWidth =
    nonNegative(
      input.viewerEnvelopeWidth,
      'viewerEnvelopeWidth',
    );

  const rootGap =
    nonNegative(
      input.rootGap,
      'rootGap',
    );

  const units =
    input.units.map(
      unit =>
        Object.freeze({
          unitId:
            unit.unitId,

          root:
            unit.root,

          x:
            finite(
              unit.x,
              `${unit.unitId}.x`,
            ),

          width:
            nonNegative(
              unit.width,
              `${unit.unitId}.width`,
            ),
        }),
    );

  const duplicateCheck =
    new Set<string>();

  for (const unit of units) {
    if (duplicateCheck.has(unit.unitId)) {
      throw new Error(
        `Duplicate geometry unit ID: ${unit.unitId}.`,
      );
    }

    duplicateCheck.add(unit.unitId);
  }

  const plannedXByUnitId =
    new Map(
      units.map(
        unit => [
          unit.unitId,
          unit.x,
        ],
      ),
    );

  const viewerEnvelope =
    viewerBounds(
      viewerCenterX,
      viewerEnvelopeWidth,
    );

  const maternalUnits =
    units.filter(
      unit =>
        unit.root === 'maternal',
    );

  const paternalUnits =
    units.filter(
      unit =>
        unit.root === 'paternal',
    );

  const initialMaternalBounds =
    boundsFor(
      maternalUnits,
      plannedXByUnitId,
    );

  const initialPaternalBounds =
    boundsFor(
      paternalUnits,
      plannedXByUnitId,
    );

  const maternalMaximumX =
    viewerEnvelope.minX -
    rootGap;

  const paternalMinimumX =
    viewerEnvelope.maxX +
    rootGap;

  const maternalShiftX =
    initialMaternalBounds &&
    initialMaternalBounds.maxX >
      maternalMaximumX
      ? maternalMaximumX -
        initialMaternalBounds.maxX
      : 0;

  const paternalShiftX =
    initialPaternalBounds &&
    initialPaternalBounds.minX <
      paternalMinimumX
      ? paternalMinimumX -
        initialPaternalBounds.minX
      : 0;

  if (maternalShiftX > 0) {
    throw new Error(
      'Maternal root shift may only move outward toward negative x.',
    );
  }

  if (paternalShiftX < 0) {
    throw new Error(
      'Paternal root shift may only move outward toward positive x.',
    );
  }

  for (const unit of maternalUnits) {
    plannedXByUnitId.set(
      unit.unitId,
      unit.x +
        maternalShiftX,
    );
  }

  for (const unit of paternalUnits) {
    plannedXByUnitId.set(
      unit.unitId,
      unit.x +
        paternalShiftX,
    );
  }

  const maternalEnvelope =
    boundsFor(
      maternalUnits,
      plannedXByUnitId,
    );

  const paternalEnvelope =
    boundsFor(
      paternalUnits,
      plannedXByUnitId,
    );

  if (
    maternalEnvelope &&
    maternalEnvelope.maxX >
      maternalMaximumX +
        0.001
  ) {
    throw new Error(
      'Maternal root envelope crosses the protected viewer boundary.',
    );
  }

  if (
    paternalEnvelope &&
    paternalEnvelope.minX <
      paternalMinimumX -
        0.001
  ) {
    throw new Error(
      'Paternal root envelope crosses the protected viewer boundary.',
    );
  }

  const unaffectedUnitIds =
    Object.freeze(
      units
        .filter(
          unit =>
            !isAncestryRoot(
              unit.root,
            ),
        )
        .map(
          unit =>
            unit.unitId,
        )
        .sort(),
    );

  return Object.freeze({
    plannedXByUnitId:
      new Map(
        plannedXByUnitId,
      ),

    viewerEnvelope,

    maternalEnvelope,

    paternalEnvelope,

    maternalShiftX,

    paternalShiftX,

    unaffectedUnitIds,

    status:
      maternalUnits.length === 0 &&
      paternalUnits.length === 0
        ? 'NO-ANCESTRY-ROOT-UNITS'
        : 'PASS',
  });
};

/*
 * CC-TREE-LIVE-003G2V
 *
 * Final presentation envelopes are separate from historical
 * ancestry-placement envelopes.
 *
 * This authority observes final normalized card geometry only.
 * It performs no placement and mutates no input.
 */

export type CanonicalTreeFinalPresentationCluster =
  | 'maternal'
  | 'paternal'
  | 'viewer';

export interface CanonicalTreeFinalPresentationCard {
  readonly geometryRoot:
    CanonicalTreeGeometryBranchRoot;

  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;

  readonly needsConnection?: boolean;
}

export interface CanonicalTreeFinalPresentationEnvelope {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;

  readonly width: number;
  readonly height: number;

  readonly cardCount: number;
}

export interface CanonicalTreeFinalClusterPresentationResult {
  readonly maternalEnvelope:
    CanonicalTreeFinalPresentationEnvelope | null;

  readonly paternalEnvelope:
    CanonicalTreeFinalPresentationEnvelope | null;

  readonly viewerEnvelope:
    CanonicalTreeFinalPresentationEnvelope | null;

  readonly includedCardCounts:
    Readonly<Record<
      CanonicalTreeFinalPresentationCluster,
      number
    >>;

  readonly excludedCardCount: number;

  readonly excludedNeedsConnectionCount:
    number;

  readonly excludedRootCounts:
    Readonly<{
      both: number;
      partnerAffiliated: number;
      affiliated: number;
      unresolved: number;
    }>;

  readonly status:
    | 'PASS'
    | 'NO-COLORED-CLUSTER-CARDS';
}

interface MutableFinalEnvelope {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  cardCount: number;
}

const finalPresentationClusterForRoot = (
  root:
    CanonicalTreeGeometryBranchRoot,
): CanonicalTreeFinalPresentationCluster | null => {
  if (root === 'maternal') {
    return 'maternal';
  }

  if (root === 'paternal') {
    return 'paternal';
  }

  if (
    root === 'self' ||
    root === 'descendant' ||
    root === 'child-mediated'
  ) {
    return 'viewer';
  }

  return null;
};

const finiteFinalGeometry = (
  value: number,
  name: string,
): number => {
  if (!Number.isFinite(value)) {
    throw new Error(
      `Final cluster geometry ${name} must be finite.`,
    );
  }

  return value;
};

const positiveFinalGeometry = (
  value: number,
  name: string,
): number => {
  const checked =
    finiteFinalGeometry(
      value,
      name,
    );

  if (checked <= 0) {
    throw new Error(
      `Final cluster geometry ${name} must be positive.`,
    );
  }

  return checked;
};

const freezeFinalEnvelope = (
  value:
    MutableFinalEnvelope | null,
): CanonicalTreeFinalPresentationEnvelope | null => {
  if (!value) {
    return null;
  }

  return Object.freeze({
    minX:
      value.minX,

    maxX:
      value.maxX,

    minY:
      value.minY,

    maxY:
      value.maxY,

    width:
      value.maxX -
      value.minX,

    height:
      value.maxY -
      value.minY,

    cardCount:
      value.cardCount,
  });
};

interface MutableExcludedRootCounts {
  both: number;
  partnerAffiliated: number;
  affiliated: number;
  unresolved: number;
}

const extendFinalEnvelope = (
  current:
    MutableFinalEnvelope | null,

  x: number,
  y: number,
  width: number,
  height: number,
): MutableFinalEnvelope => {
  const minX =
    x -
    width / 2;

  const maxX =
    x +
    width / 2;

  const minY =
    y -
    height / 2;

  const maxY =
    y +
    height / 2;

  if (!current) {
    return {
      minX,
      maxX,
      minY,
      maxY,
      cardCount:
        1,
    };
  }

  return {
    minX:
      Math.min(
        current.minX,
        minX,
      ),

    maxX:
      Math.max(
        current.maxX,
        maxX,
      ),

    minY:
      Math.min(
        current.minY,
        minY,
      ),

    maxY:
      Math.max(
        current.maxY,
        maxY,
      ),

    cardCount:
      current.cardCount +
      1,
  };
};

const incrementExcludedRootCount = (
  current:
    MutableExcludedRootCounts,

  root:
    CanonicalTreeGeometryBranchRoot,
): MutableExcludedRootCounts => {
  const next = {
    ...current,
  };

  switch (root) {
    case 'both':
      next.both += 1;
      break;

    case 'partner-affiliated':
      next.partnerAffiliated += 1;
      break;

    case 'affiliated':
      next.affiliated += 1;
      break;

    case 'unresolved':
      next.unresolved += 1;
      break;

    default:
      break;
  }

  return next;
};

interface MutableFinalClusterPresentationState {
  envelopes:
    Record<
      CanonicalTreeFinalPresentationCluster,
      MutableFinalEnvelope | null
    >;

  includedCardCounts:
    Record<
      CanonicalTreeFinalPresentationCluster,
      number
    >;

  excludedCardCount:
    number;

  excludedNeedsConnectionCount:
    number;

  excludedRootCounts:
    MutableExcludedRootCounts;
}

const createFinalClusterPresentationState = (
): MutableFinalClusterPresentationState => ({
  envelopes: {
    maternal:
      null,

    paternal:
      null,

    viewer:
      null,
  },

  includedCardCounts: {
    maternal:
      0,

    paternal:
      0,

    viewer:
      0,
  },

  excludedCardCount:
    0,

  excludedNeedsConnectionCount:
    0,

  excludedRootCounts: {
    both:
      0,

    partnerAffiliated:
      0,

    affiliated:
      0,

    unresolved:
      0,
  },
});

const observeFinalPresentationCard = (
  state:
    MutableFinalClusterPresentationState,

  card:
    CanonicalTreeFinalPresentationCard,
): void => {
  const x =
    finiteFinalGeometry(
      card.x,
      'card.x',
    );

  const y =
    finiteFinalGeometry(
      card.y,
      'card.y',
    );

  const width =
    positiveFinalGeometry(
      card.width,
      'card.width',
    );

  const height =
    positiveFinalGeometry(
      card.height,
      'card.height',
    );

  if (card.needsConnection) {
    state.excludedCardCount +=
      1;

    state.excludedNeedsConnectionCount +=
      1;

    return;
  }

  const cluster =
    finalPresentationClusterForRoot(
      card.geometryRoot,
    );

  if (!cluster) {
    state.excludedCardCount +=
      1;

    state.excludedRootCounts =
      incrementExcludedRootCount(
        state.excludedRootCounts,
        card.geometryRoot,
      );

    return;
  }

  state.envelopes[cluster] =
    extendFinalEnvelope(
      state.envelopes[cluster],
      x,
      y,
      width,
      height,
    );

  state.includedCardCounts[
    cluster
  ] += 1;
};

export const deriveCanonicalTreeFinalClusterPresentationEnvelopes = (
  input: {
    readonly cards:
      readonly CanonicalTreeFinalPresentationCard[];
  },
): CanonicalTreeFinalClusterPresentationResult => {
  const state =
    createFinalClusterPresentationState();

  for (const card of input.cards) {
    observeFinalPresentationCard(
      state,
      card,
    );
  }

  const coloredCardCount =
    state.includedCardCounts.maternal +
    state.includedCardCounts.paternal +
    state.includedCardCounts.viewer;

  return Object.freeze({
    maternalEnvelope:
      freezeFinalEnvelope(
        state.envelopes.maternal,
      ),

    paternalEnvelope:
      freezeFinalEnvelope(
        state.envelopes.paternal,
      ),

    viewerEnvelope:
      freezeFinalEnvelope(
        state.envelopes.viewer,
      ),

    includedCardCounts:
      Object.freeze({
        maternal:
          state.includedCardCounts.maternal,

        paternal:
          state.includedCardCounts.paternal,

        viewer:
          state.includedCardCounts.viewer,
      }),

    excludedCardCount:
      state.excludedCardCount,

    excludedNeedsConnectionCount:
      state.excludedNeedsConnectionCount,

    excludedRootCounts:
      Object.freeze({
        both:
          state.excludedRootCounts.both,

        partnerAffiliated:
          state.excludedRootCounts.partnerAffiliated,

        affiliated:
          state.excludedRootCounts.affiliated,

        unresolved:
          state.excludedRootCounts.unresolved,
      }),

    status:
      coloredCardCount > 0
        ? 'PASS'
        : 'NO-COLORED-CLUSTER-CARDS',
  });
};

/*
 * CC-TREE-LIVE-003G2V
 *
 * Final cluster line presentation authority.
 *
 * KinsCircle uses the already-routed canonical family connectors as the
 * visual line language. This authority never creates topology or route
 * geometry.
 *
 * Color is semantic presentation only:
 *
 *   maternal source cluster -> maternal pink
 *   paternal source cluster -> paternal blue
 *   viewer source cluster   -> viewer purple
 *
 * Mixed, affiliated, both-side, or unresolved source authority fails
 * closed to neutral presentation.
 *
 * No member ID, parent-array order, stored label, or coordinate may
 * select a presentation tone.
 */

export type CanonicalTreeFinalClusterLineTone =
  | 'maternal-pink'
  | 'paternal-blue'
  | 'viewer-purple'
  | 'neutral';

export interface CanonicalTreeFinalClusterLinePresentationInput {
  readonly sourceRoots:
    readonly CanonicalTreeGeometryBranchRoot[];
}

export interface CanonicalTreeFinalClusterLinePresentationResult {
  readonly cluster:
    CanonicalTreeFinalPresentationCluster | null;

  readonly tone:
    CanonicalTreeFinalClusterLineTone;

  readonly disposition:
    | 'CANONICAL-CLUSTER'
    | 'NO-SOURCE-ROOT'
    | 'EXCLUDED-SOURCE-ROOT'
    | 'MIXED-SOURCE-CLUSTERS';
}

const lineToneForCluster = (
  cluster:
    CanonicalTreeFinalPresentationCluster,
): CanonicalTreeFinalClusterLineTone => {
  if (cluster === 'maternal') {
    return 'maternal-pink';
  }

  if (cluster === 'paternal') {
    return 'paternal-blue';
  }

  return 'viewer-purple';
};

export const resolveCanonicalTreeFinalClusterLinePresentation = (
  input:
    CanonicalTreeFinalClusterLinePresentationInput,
): CanonicalTreeFinalClusterLinePresentationResult => {
  if (input.sourceRoots.length === 0) {
    return Object.freeze({
      cluster:
        null,

      tone:
        'neutral',

      disposition:
        'NO-SOURCE-ROOT',
    });
  }

  const clusters =
    new Set<
      CanonicalTreeFinalPresentationCluster
    >();

  let hasExcludedRoot =
    false;

  for (const root of input.sourceRoots) {
    const cluster =
      finalPresentationClusterForRoot(
        root,
      );

    if (!cluster) {
      hasExcludedRoot =
        true;

      continue;
    }

    clusters.add(
      cluster,
    );
  }

  if (hasExcludedRoot) {
    return Object.freeze({
      cluster:
        null,

      tone:
        'neutral',

      disposition:
        'EXCLUDED-SOURCE-ROOT',
    });
  }

  if (clusters.size !== 1) {
    return Object.freeze({
      cluster:
        null,

      tone:
        'neutral',

      disposition:
        'MIXED-SOURCE-CLUSTERS',
    });
  }

  const cluster =
    [...clusters][0];

  return Object.freeze({
    cluster,

    tone:
      lineToneForCluster(
        cluster,
      ),

    disposition:
      'CANONICAL-CLUSTER',
  });
};
